import React from 'react';
import { Link } from 'react-router-dom';
import { preloadManifesto } from '../../lib/manifestoPreload';
import { prefetchSectionData, prefetchSectionChunk } from '../../lib/sectionPrefetch';
import { useLang, withLang } from '../../lib/lang';

/**
 * Big navigation menu shown inside each side of the hero split-screen.
 *
 * Layout: vertical column of oversized, monospaced-uppercase links,
 * each with a subtle bar-slide hover animation. Kept POINTER-EVENTS
 * enabled while the surrounding `.side-content` block is not — so the
 * split-screen hover-respiro (which listens on `.split-container`)
 * still fires even when the cursor is over a menu item.
 */
export default function SideMenu({ world, items, testId }) {
    const lang = useLang();
    const colourClass =
        world === 'cinzia' ? 'side-menu--white' : 'side-menu--ink';
    // iter90 · Strategia C — Intent-based prefetch. Hover/focus/touch
    // triggers: (a) manifesto preload (already existing, session cache
    // 24h), (b) SectionPage chunk prefetch (webpack dynamic import;
    // idempotent, no-op if already downloaded via idle prefetch), (c)
    // apiCache warm-fetch of the section data. All three are dedupe-
    // safe. Fires for EVERY link — Bio/Vision/Journey usually already
    // warm from idle prefetch, so hover is a no-op for those; Materia
    // and Portfolio benefit the most (not in idle prefetch list).
    const onIntent = (slug) => {
        preloadManifesto(slug);
        prefetchSectionChunk();
        prefetchSectionData(slug, lang);
    };
    return (
        <nav
            className={`side-menu ${colourClass}`}
            data-testid={testId || `side-menu-${world}`}
            aria-label={`${world} navigation`}
        >
            <ul className="side-menu-list">
                {items.map((item) => (
                    <li key={item.slug} className="side-menu-item">
                        <Link
                            to={withLang(item.path, lang)}
                            className="side-menu-link"
                            data-testid={`menu-link-${item.slug}`}
                            onMouseEnter={() => onIntent(item.slug)}
                            onFocus={() => onIntent(item.slug)}
                            onTouchStart={() => onIntent(item.slug)}
                        >
                            <span className="side-menu-link-bar" aria-hidden="true" />
                            <span className="side-menu-link-label">
                                {typeof item.label === 'object'
                                    ? (item.label[lang] || item.label.it)
                                    : item.label}
                            </span>
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

// Static menu definitions — kept in code (not DB) because they are
// structural, not editorial. Order matches user's Phase 1 brief.
// Labels are bilingual `{ it, en }` — resolved at render via useLang().
export const CINZIA_MENU = [
    { slug: 'cinzia-bio',     label: { it: 'Bio',        en: 'Bio' },        path: '/cinzia/bio' },
    { slug: 'cinzia-journey', label: { it: 'My Journey', en: 'My Journey' }, path: '/cinzia/my-journey' },
    { slug: 'cinzia-vision',  label: { it: 'My Vision',  en: 'My Vision' },  path: '/cinzia/my-vision' },
];

export const ALLDESIGN_MENU = [
    { slug: 'alldesign-chisiamo',  label: { it: 'Chi Siamo', en: 'Who We Are' }, path: '/alldesign/chi-siamo' },
    { slug: 'alldesign-vision',    label: { it: 'Vision',    en: 'Vision' },     path: '/alldesign/vision' },
    { slug: 'alldesign-materia',   label: { it: 'Materia',   en: 'Materia' },    path: '/alldesign/materia' },
    /* iter56 · Universal EN — nav labels stay localised only where
       IT and EN have distinct copy the client wants (Chi Siamo).
       Ruolo → "Our Role" everywhere, matching the H1 iter53 change. */
    { slug: 'alldesign-ruolo',     label: { it: 'Our Role',  en: 'Our Role' },   path: '/alldesign/ruolo' },
    { slug: 'alldesign-portfolio', label: { it: 'Portfolio', en: 'Portfolio' },  path: '/alldesign/portfolio' },
];
